<template>
  <CardWithTitle
    :item-id="itemId"
    :items="items"
    :map-keys="mapKeys"
    :request-status="requestStatus"
    :options-data="cardOptions"
    :has-data-to-export="hasDataToExport"
    class="make-a-copy"
    @call-option-menu="callOptionMenu"
    @reload="getData"
  >
    <v-data-table
      v-if="hasItems"
      :headers="headers"
      :items="items"
      :items-per-page="itemsPerPage"
      :loading="inputLoading"
      class="table-truncate"
    >
      <template v-slot:[`item.bot`]="{ item }">
        <span>{{ item.bot }}</span>
      </template>
      <template v-slot:[`item.name`]="{ item }">
        <span>{{ item.name }}</span>
      </template>
      <template v-slot:[`item.waitingTickets`]="{ item }">
        <span>{{ item.waitingTickets }}</span>
      </template>
      <template v-slot:[`item.openedTickets`]="{ item }">
        <span>{{ item.openedTickets }}</span>
      </template>
      <template v-slot:[`item.averageResponseTime`]="{ item }">
        <span>{{ item.averageResponseTime }}</span>
      </template>
      <template v-slot:[`item.averageAttendanceTime`]="{ item }">
        <span>{{ item.averageAttendanceTime }}</span>
      </template>
    </v-data-table>

    <template #top-right-slot>
      <Badge
        v-if="hasFilterApplied && isEditor"
        type="info"
        :tooltip="$t('components.Badge.label.tooltip.info')"
        :value="$t('components.Badge.label.icon.key')"
        :preset="true"
      />
    </template>
  </CardWithTitle>
</template>

<script>
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import blipModel from '@/mixins/blip-model';
import { mapActions } from 'vuex';
import { timeToSeconds, secondsToTime } from '@/utils/formatter';
import exportModel from '@/mixins/export-model';
import cardsModel from '@/mixins/cards-model';
import Badge from '@/components/Badge';

export default {
  name: 'BlipMonitoringTeamsTable',
  components: {
    CardWithTitle,
    Badge,
  },
  mixins: [
    exportModel,
    cardsModel,
    blipModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    hasDataToExport: {
      type: Boolean,
      default: true,
    },
    servicePath: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      headers: [
        { text: this.$t('components.BlipMonitoringTeamsTable.bot'), value: 'bot' },
        { text: this.$t('components.BlipMonitoringTeamsTable.name'), value: 'name' },
        { text: this.$t('components.BlipMonitoringTeamsTable.waitingTickets'), value: 'waitingTickets' },
        { text: this.$t('components.BlipMonitoringTeamsTable.openedTickets'), value: 'openedTickets' },
        { text: this.$t('components.BlipMonitoringTeamsTable.averageAttendanceTime'), value: 'averageAttendanceTime' },
        { text: this.$t('components.BlipMonitoringTeamsTable.averageResponseTime'), value: 'averageResponseTime' },
      ],
      rawSeries: [],
      dialog: false,
      itemsPerPage: 5,
      requestStatus: 'loading',
      cardFilters: {},
      mapKeys: {
        bot: this.$t('components.BlipMonitoringTeamsTable.bot'),
        name: this.$t('components.BlipMonitoringTeamsTable.name'),
        waitingTickets: this.$t('components.BlipMonitoringTeamsTable.waitingTickets'),
        openedTickets: this.$t('components.BlipMonitoringTeamsTable.openedTickets'),
        averageAttendanceTime: this.$t('components.BlipMonitoringTeamsTable.averageAttendanceTime'),
        averageResponseTime: this.$t('components.BlipMonitoringTeamsTable.averageResponseTime'),
      },
    };
  },
  computed: {
    inputLoading() {
      return this.requestStatus === 'loading';
    },
    hasItems() {
      return Boolean(this.items.length);
    },
    cardOptions() {
      if (this.hasDataToExport) {
        return this.baseCardOptions.concat(this.exportWithXLSXOptionsData(this.items)).sort((actualOption, prevOption) => this.sortMenuOptions(actualOption, prevOption));
      }
      return this.baseCardOptions;
    },
    items() {
      return this.rawSeries.flatMap((obj) => {
        const bot = this.getBotName(obj.to, this.cardFilters);
        const resourceItems = obj.resource.items.map((item) => {
          return {
            bot,
            name: item.name,
            waitingTickets: item.waitingTickets,
            openedTickets: item.openedTickets,
            averageAttendanceTime: secondsToTime(timeToSeconds(item.averageAttendanceTime)),
            averageResponseTime: secondsToTime(timeToSeconds(item.averageResponseTime)),
          };
        });
        return resourceItems;
      });
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getBlipService',
      'getAppliedCardFilter',
      'hasNoBlipBotAndContractFiltered',
    ]),
    exportWithXLSXOptionsData(items) {
      const exportExtensionOptions = this.exportExtensions.map((extension, index) => (
        {
          order: index + 1,
          name: extension.type,
          function: () => (this.exportComponent(extension, true)),
        }
      ));

      exportExtensionOptions.push({
        order: exportExtensionOptions.length + 1,
        name: 'XLSX',
        function: () => (this.exportComponentXLSX(items)),
      });

      return [
        {
          order: 5,
          name: this.$t('mixins.exportModel.label.exportLabel'),
          iconname: 'download',
          function: () => ([]),
          child: exportExtensionOptions,
          trackName: 'ST CardMenu Export Click',
          trackProperties: {
            title: this.card?.props?.title,
            widget: this.card?.component,
          },
          isExpandedModeOption: true,
        },
      ];
    },
    async getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      if (await this.hasNoBlipBotAndContractFiltered(this.itemId)) {
        this.requestStatus = 'emptyBot';
        this.items = [];
        return;
      }

      this.cardFilters = await this.getAppliedCardFilter(this.itemId);

      this.getBlipService({
        servicePath: this.servicePath,
        itemId: this.itemId,
      })
        .then((responses) => {
          if (!options?.silentRefresh) {
            this.requestStatus = 'loading';
          }

          if (this.totalResponseItems(responses)) {
            this.requestStatus = 'emptyData';
            return;
          }

          this.rawSeries = responses;

          this.requestStatus = 'success';
        })
        .catch(error => {
          console.error(error);
          this.items = [];
          this.requestStatus = error.message;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .table-truncate {
    td {
      max-width: 10vw;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .modal-style {
    * {
      color: var(--ds-color-snow) !important;
    }

    h3 {
      font-weight: var(--ds-font-weight-bold);
    }
  }
}
</style>
